import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import { templatesApi, masterTemplatesApi, type ReportCardTemplate, type ReportCardMasterTemplate } from "../../../service/report-card"
import { getAllSessions } from "../../../service/sessions"
import { getAllClasses } from "../../../service/classes"
import { getAllTerms } from "../../../service/term-management"
import { selectCurrentBranch, selectBranches } from "../../../core/data/redux/slice/branchesSlice"
import { all_routes as routes } from "../../router/all_routes"

// ── Shared confirm modal ────────────────────────────────────────────
function ConfirmModal({ open, title, body, danger, onConfirm, onCancel }: {
  open: boolean; title: string; body: string; danger?: string
  onConfirm: () => void; onCancel: () => void
}) {
  if (!open) return null
  return (
    <>
      <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center p-4">
              <span className="text-danger" style={{ fontSize: 40 }}><i className="ti ti-trash-x" /></span>
              <h4 className="mt-2">{title}</h4>
              <p className="text-muted">{body}</p>
              <div className="d-flex justify-content-center gap-2 mt-2">
                <button className="btn btn-light" onClick={onCancel}>Cancel</button>
                <button className="btn btn-danger" onClick={onConfirm}>{danger || "Yes, Delete"}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

// ── Start-from-master modal ─────────────────────────────────────────
function StartFromMasterModal({ open, masters, onClose, onPick }: {
  open: boolean
  masters: ReportCardMasterTemplate[]
  onClose: () => void
  onPick: (masterId: string | null) => void
}) {
  if (!open) return null
  return (
    <>
      <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Start new class template</h5>
              <button className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <p className="text-muted small mb-3">
                Pick a master template — its layers (logo, header, etc.) appear as read-only background layers.
                Choose "Start from scratch" for a fully independent design.
              </p>
              <div className="d-flex flex-column gap-2">
                <button
                  className="btn btn-outline-secondary text-start p-3"
                  onClick={() => onPick(null)}
                >
                  <div className="fw-semibold">Start from scratch</div>
                  <div className="text-muted small">Empty canvas — design the whole layout from zero.</div>
                </button>
                {masters.length === 0 ? (
                  <div className="text-center text-muted small py-3 border border-dashed rounded">
                    No master templates yet. Switch to the Master templates tab to create one first.
                  </div>
                ) : (
                  masters.map(m => (
                    <button
                      key={m._id}
                      className="btn btn-outline-primary text-start p-3"
                      onClick={() => onPick(m._id)}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <i className="ti ti-folder text-warning" />
                        <span className="fw-semibold">{m.name}</span>
                        {m.scopeLabel && (
                          <span className="badge bg-primary-transparent text-primary ms-1">{m.scopeLabel}</span>
                        )}
                        <span className="ms-auto text-muted small">{m.layerCount ?? 0} layers</span>
                      </div>
                      {m.notes && <div className="text-muted small mt-1 text-truncate">{m.notes}</div>}
                    </button>
                  ))
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-light" onClick={onClose}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

// ── Clone modal ─────────────────────────────────────────────────────
function CloneModal({ source, sessions, terms, classes, onClose, onCloned }: {
  source: ReportCardTemplate | null
  sessions: any[]; terms: any[]; classes: any[]
  onClose: () => void; onCloned: () => void
}) {
  const [sessionId, setSessionId] = useState("")
  const [termId, setTermId] = useState("")
  const [classId, setClassId] = useState("")
  const [name, setName] = useState("")
  const [nameDirty, setNameDirty] = useState(false)
  const [saving, setSaving] = useState(false)

  const autoName = (clsId: string, tId: string) => {
    const cls = classes.find(c => c._id === clsId)
    const term = terms.find(t => t._id === tId)
    return cls && term ? `${cls.name} ${ (term as any).title || (term as any).name}` : ""
  }

  // Populate from source when it changes
  useEffect(() => {
    if (!source) return
    const srcSession = (source.session as any)?._id || (source.session as any) || ""
    const srcTerm = (source.term as any)?._id || (source.term as any) || ""
    const srcClass = (source.classes as any[])?.[0]?._id || (source.classes as any[])?.[0] || ""
    setSessionId(srcSession)
    setTermId(srcTerm)
    setClassId(srcClass)
    setName(`Copy of ${source.name}`)
    setNameDirty(false)
    setSaving(false)
  }, [source?._id])

  const handleClassOrTermChange = (field: "class" | "term", val: string) => {
    const newClassId = field === "class" ? val : classId
    const newTermId = field === "term" ? val : termId
    if (field === "class") setClassId(val)
    else setTermId(val)
    if (!nameDirty) {
      const derived = autoName(newClassId, newTermId)
      if (derived) setName(derived)
    }
  }

  const submit = async () => {
    if (!sessionId || !termId || !classId) { toast.error("Pick session, term, and class"); return }
    if (!name.trim()) { toast.error("Name cannot be empty"); return }
    setSaving(true)
    try {
      await templatesApi.clone(source!._id, { name: name.trim(), session: sessionId, term: termId, classes: [classId] })
      toast.success(`Cloned as "${name.trim()}"`)
      onCloned()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Clone failed")
    } finally {
      setSaving(false)
    }
  }

  if (!source) return null
  return (
    <>
      <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Copy "{source.name}"</h5>
              <button className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <p className="text-muted small mb-3">
                Pick the destination scope. Change only the dimension(s) you need — the layers are copied as-is.
              </p>
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label form-label-sm">Session</label>
                  <select className="form-select form-select-sm" value={sessionId} onChange={e => setSessionId(e.target.value)}>
                    <option value="">— Pick session —</option>
                    {sessions.map(s => <option key={s._id} value={s._id}>{s.title}</option>)}
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label form-label-sm">Exam term</label>
                  <select className="form-select form-select-sm" value={termId} onChange={e => handleClassOrTermChange("term", e.target.value)}>
                    <option value="">— Pick term —</option>
                    {terms.map(t => <option key={t._id} value={t._id}>{(t as any).title || (t as any).name}</option>)}
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label form-label-sm">Class</label>
                  <select className="form-select form-select-sm" value={classId} onChange={e => handleClassOrTermChange("class", e.target.value)}>
                    <option value="">— Pick class —</option>
                    {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label form-label-sm">
                    Name
                    {!nameDirty && <span className="text-muted ms-1 fw-normal">(auto-derived · type to override)</span>}
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={name}
                    onChange={e => {
                      setName(e.target.value)
                      setNameDirty(e.target.value.trim().length > 0)
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-light" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={submit} disabled={saving}>
                {saving ? "Copying…" : "Copy"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

// ── New master modal ────────────────────────────────────────────────
function NewMasterModal({ open, onClose, onCreated }: {
  open: boolean; onClose: () => void; onCreated: (m: ReportCardMasterTemplate) => void
}) {
  const [name, setName] = useState("")
  const [scopeLabel, setScopeLabel] = useState("")
  const [notes, setNotes] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) { setName(""); setScopeLabel(""); setNotes(""); setSaving(false) }
  }, [open])

  const submit = async () => {
    if (!name.trim()) { toast.error("Name is required"); return }
    setSaving(true)
    try {
      const r = await masterTemplatesApi.create({ name: name.trim(), scopeLabel: scopeLabel.trim() || undefined, notes: notes.trim() || undefined })
      toast.success(`Created master "${name.trim()}"`)
      onCreated(r.data)
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Create failed")
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null
  const LABEL_SUGGESTIONS = ["Primary", "Secondary", "Higher Secondary", "Montessori"]
  return (
    <>
      <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">New master template</h5>
              <button className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <p className="text-muted small mb-3">
                A master stores the layout pieces every class template should share — logo, page header, watermark, etc.
                Design it in the canvas editor after creating.
              </p>
              <div className="mb-3">
                <label className="form-label form-label-sm">Name <span className="text-danger">*</span></label>
                <input type="text" className="form-control form-control-sm" placeholder="e.g. Primary report card 2025-26"
                  value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div className="mb-3">
                <label className="form-label form-label-sm">Scope label <span className="text-muted fw-normal">(free-text grouping tag)</span></label>
                <input type="text" className="form-control form-control-sm" placeholder="e.g. Primary"
                  value={scopeLabel} onChange={e => setScopeLabel(e.target.value)} />
                <div className="mt-1 d-flex flex-wrap gap-1">
                  {LABEL_SUGGESTIONS.map(l => (
                    <button key={l} type="button" className="btn btn-sm btn-outline-secondary py-0 px-2" style={{ fontSize: 11 }}
                      onClick={() => setScopeLabel(l)}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="mb-1">
                <label className="form-label form-label-sm">Notes <span className="text-muted fw-normal">(optional)</span></label>
                <textarea className="form-control form-control-sm" rows={2} placeholder="e.g. Uses pill-style header. Logo top-left."
                  value={notes} onChange={e => setNotes(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-light" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={submit} disabled={saving}>
                {saving ? "Creating…" : "Create + open designer"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

// ── Main page ───────────────────────────────────────────────────────
const ReportCardDesigner = () => {
  const navigate = useNavigate()
  const currentBranch = useSelector(selectCurrentBranch)

  const [activeTab, setActiveTab] = useState<"class" | "master">("class")

  // Class templates
  const [items, setItems] = useState<ReportCardTemplate[]>([])
  const [loadingClass, setLoadingClass] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [cloneSource, setCloneSource] = useState<ReportCardTemplate | null>(null)
  const [showStartModal, setShowStartModal] = useState(false)

  // Master templates
  const [masterItems, setMasterItems] = useState<ReportCardMasterTemplate[]>([])
  const [loadingMaster, setLoadingMaster] = useState(false)
  const [deleteMasterId, setDeleteMasterId] = useState<string | null>(null)
  const [showNewMasterModal, setShowNewMasterModal] = useState(false)

  // Cascade filters
  const [sessionId, setSessionId] = useState("")
  const [campusId, setCampusId] = useState("")
  const [termId, setTermId] = useState("")
  const [classId, setClassId] = useState("")
  const branches = useSelector(selectBranches)
  // Default the Campus filter to the active branch.
  useEffect(() => { if (!campusId && currentBranch?._id) setCampusId(currentBranch._id) }, [currentBranch?._id])
  const [sessions, setSessions] = useState<any[]>([])
  const [terms, setTerms] = useState<any[]>([])
  const [classes, setClasses] = useState<any[]>([])

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [s, t, cl] = await Promise.all([getAllSessions(), getAllTerms(), getAllClasses()])
        setSessions(Array.isArray(s) ? s : (s as any)?.sessions || (s as any)?.data || [])
        setTerms(Array.isArray(t) ? t : (t as any)?.data || (t as any)?.terms || [])
        setClasses(Array.isArray(cl) ? cl : (cl as any)?.classes || (cl as any)?.data || [])
      } catch {}
    }
    fetchFilters()
  }, [])

  const fetchClassTemplates = async () => {
    setLoadingClass(true)
    try {
      const params: Record<string, any> = {}
      const branchFilter = campusId || currentBranch?._id
      if (branchFilter) params.branch = branchFilter
      if (sessionId) params.session = sessionId
      if (termId) params.term = termId
      if (classId) params.class = classId
      const r = await templatesApi.list(params)
      setItems(r?.data || [])
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to load templates")
    } finally {
      setLoadingClass(false)
    }
  }

  const fetchMasterTemplates = async () => {
    setLoadingMaster(true)
    try {
      const r = await masterTemplatesApi.list()
      setMasterItems(r?.data || [])
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to load master templates")
    } finally {
      setLoadingMaster(false)
    }
  }

  // Always fetch master templates (needed for the StartFromMaster modal on the class tab)
  useEffect(() => { fetchMasterTemplates() }, [])

  useEffect(() => {
    if (activeTab === "class") fetchClassTemplates()
    else fetchMasterTemplates()
  }, [activeTab, sessionId, campusId, termId, classId, currentBranch?._id])

  // Cascade: reset downstream when upstream changes
  const handleSessionChange = (v: string) => { setSessionId(v); setTermId(""); setClassId("") }
  const handleTermChange = (v: string) => { setTermId(v); setClassId("") }

  const allScopePicked = !!(sessionId && termId && classId)
  const missingScope = [!sessionId && "session", !termId && "term", !classId && "class"].filter(Boolean)

  // Delete class template
  const handleDelete = async () => {
    if (!deleteId) return
    try {
      await templatesApi.remove(deleteId)
      toast.success("Deleted")
      setDeleteId(null)
      fetchClassTemplates()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Delete failed")
    }
  }

  // Delete master template
  const handleDeleteMaster = async () => {
    if (!deleteMasterId) return
    try {
      await masterTemplatesApi.remove(deleteMasterId)
      toast.success("Deleted")
      setDeleteMasterId(null)
      fetchMasterTemplates()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Delete failed")
    }
  }

  // "+ New Design" flow
  const handleNewDesign = () => {
    if (!allScopePicked) {
      toast.warning(`Pick ${missingScope.join(", ")} first to enable a new design`)
      return
    }
    setShowStartModal(true)
  }

  const startNewWith = (masterId: string | null) => {
    setShowStartModal(false)
    const qs = new URLSearchParams({ session: sessionId, term: termId, class: classId })
    if (masterId) qs.set("from_master", masterId)
    navigate(`${routes.reportCardDesignerEdit.replace(":id", "new")}?${qs.toString()}`)
  }

  // Group class templates by master
  const grouped = useMemo(() => {
    const m = new Map<string, { masterId: string | null; masterName: string | null; masterScopeLabel: string | null; templates: ReportCardTemplate[] }>()
    for (const t of items) {
      const masterRef = t.masterTemplate as any
      const key = masterRef?._id || "__none__"
      const existing = m.get(key) || { masterId: masterRef?._id || null, masterName: masterRef?.name || null, masterScopeLabel: masterRef?.scopeLabel || null, templates: [] as ReportCardTemplate[] }
      existing.templates.push(t)
      m.set(key, existing)
    }
    // Order templates by their class's stage order (matches RMS exactly:
    // Play Group → Montessori → Grade I…V); fall back to natural name sort.
    const natural = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
    const classOrder = (t: ReportCardTemplate) => {
      const o = (t.classes as any[])?.[0]?.order
      return Number.isFinite(o) ? o : Number.MAX_SAFE_INTEGER
    }
    const groups = Array.from(m.values())
    for (const g of groups) g.templates.sort((x, y) => {
      const ox = classOrder(x), oy = classOrder(y)
      return ox !== oy ? ox - oy : natural(x.name || "", y.name || "")
    })
    return groups.sort((a, b) => {
      if (!a.masterId && b.masterId) return 1
      if (a.masterId && !b.masterId) return -1
      return natural(String(a.masterName || ""), String(b.masterName || ""))
    })
  }, [items])

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div>
            <h3 className="page-title mb-1">Report Card Designer</h3>
            <p className="text-muted mb-0">
              Designs that print onto A4. Master templates are reusable layouts —
              class templates apply them per session × term × class.
            </p>
          </div>
          {activeTab === "class" ? (
            <div className="d-flex flex-column align-items-end gap-1">
              <button
                className={`btn ${allScopePicked ? "btn-primary" : "btn-secondary"}`}
                onClick={handleNewDesign}
                title={!allScopePicked ? `Pick ${missingScope.join(", ")} first` : undefined}
              >
                <i className="ti ti-square-rounded-plus me-2" />
                + New Design
              </button>
              {!allScopePicked && (
                <span className="text-warning small fst-italic">
                  Pick {missingScope.join(", ")} below to enable
                </span>
              )}
            </div>
          ) : (
            <button className="btn btn-primary" onClick={() => setShowNewMasterModal(true)}>
              <i className="ti ti-square-rounded-plus me-2" />
              + New Master
            </button>
          )}
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button className={`nav-link ${activeTab === "class" ? "active" : ""}`} onClick={() => setActiveTab("class")}>
              Class Templates
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link ${activeTab === "master" ? "active" : ""}`} onClick={() => setActiveTab("master")}>
              Master Templates
            </button>
          </li>
        </ul>

        {/* ── Class templates tab ── */}
        {activeTab === "class" && (
          <>
            {/* Cascade filters */}
            <div className="card mb-3">
              <div className="card-body py-3">
                <div className="row g-3 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label mb-1">Session</label>
                    <select className="form-select" value={sessionId} onChange={e => handleSessionChange(e.target.value)}>
                      <option value="">— Any session —</option>
                      {sessions.map(s => <option key={s._id} value={s._id}>{s.title}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label mb-1">Campus</label>
                    <select className="form-select" value={campusId} onChange={e => setCampusId(e.target.value)}>
                      <option value="">— Any campus —</option>
                      {branches.map((b: any) => <option key={b._id} value={b._id}>{b.name}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label mb-1">
                      Exam Term
                      {!sessionId && <span className="text-muted small ms-1">(pick session first)</span>}
                    </label>
                    <select className="form-select" value={termId} onChange={e => handleTermChange(e.target.value)} disabled={!sessionId}>
                      <option value="">— Any term —</option>
                      {terms.map(t => <option key={t._id} value={t._id}>{(t as any).title || (t as any).name}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label mb-1">
                      Class
                      {!termId && <span className="text-muted small ms-1">(pick term first)</span>}
                    </label>
                    <select className="form-select" value={classId} onChange={e => setClassId(e.target.value)} disabled={!termId}>
                      <option value="">— Any class —</option>
                      {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Template groups */}
            {loadingClass ? (
              <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
            ) : items.length === 0 ? (
              <div className="card">
                <div className="card-body text-center py-5 text-muted">
                  {allScopePicked
                    ? "No templates found for this scope. Click + New Design to create one."
                    : "No templates found. Pick a scope above and click + New Design to create one."}
                </div>
              </div>
            ) : (
              <div className="d-flex flex-column gap-4">
                {grouped.map(group => (
                  <div key={group.masterId || "__none__"}>
                    {/* Group header with clickable master link */}
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <i className="ti ti-folder text-warning" style={{ fontSize: 18 }} />
                      {group.masterId ? (
                        <>
                          <span className="text-muted">Master:</span>
                          <button
                            className="btn btn-link p-0 fw-semibold text-primary"
                            style={{ fontSize: "inherit", textDecoration: "none" }}
                            onClick={() => navigate(routes.reportCardDesignerMasterEdit.replace(":masterId", group.masterId!))}
                          >
                            {group.masterName}
                          </button>
                          {group.masterScopeLabel && (
                            <span className="badge bg-primary-transparent text-primary">{group.masterScopeLabel}</span>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">Ad-hoc (no master)</span>
                      )}
                      <span className="badge bg-secondary-transparent">
                        {group.templates.length} template{group.templates.length !== 1 ? "s" : ""}
                      </span>
                    </div>

                    {/* Class template table */}
                    <div className="card">
                      <div className="table-responsive">
                        <table className="table table-sm table-hover mb-0">
                          <thead className="table-light text-uppercase small">
                            <tr>
                              <th>Name</th>
                              <th>Session</th>
                              <th>Campus</th>
                              <th>Term</th>
                              <th>Classes</th>
                              <th className="text-end">Layers</th>
                              <th>Updated</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.templates.map(t => (
                              <tr key={t._id}>
                                <td className="fw-medium">{t.name}</td>
                                <td className="text-muted">{(t.session as any)?.title || "—"}</td>
                                <td className="text-muted">{(t.branch as any)?.name || currentBranch?.name || "—"}</td>
                                <td className="text-muted">{(t.term as any)?.title || (t.term as any)?.name || "—"}</td>
                                <td className="text-muted">
                                  {Array.isArray(t.classes) && t.classes.length
                                    ? t.classes.map((c: any) => c.name || c).join(", ")
                                    : "—"}
                                </td>
                                <td className="text-end font-monospace">{t.layerCount ?? 0}</td>
                                <td className="text-muted small">{new Date(t.updatedAt!).toLocaleDateString()}</td>
                                <td>
                                  <div className="d-flex gap-1 justify-content-end">
                                    <button className="btn btn-sm btn-primary" onClick={() => navigate(routes.reportCardDesignerEdit.replace(":id", t._id))}>
                                      Edit
                                    </button>
                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setCloneSource(t)}>
                                      Copy
                                    </button>
                                    <button className="btn btn-sm btn-outline-danger" onClick={() => setDeleteId(t._id)}>
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Master templates tab ── */}
        {activeTab === "master" && (
          <>
            {loadingMaster ? (
              <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
            ) : masterItems.length === 0 ? (
              <div className="card">
                <div className="card-body text-center py-5 text-muted">
                  No master templates yet. Click + New Master to design your first one — pick a label like "Primary" / "Secondary",
                  then drop the logo, page header and layout elements you want every class to share.
                </div>
              </div>
            ) : (
              <div className="card">
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light text-uppercase small">
                      <tr>
                        <th>Name</th>
                        <th>Scope Label</th>
                        <th className="text-end">Layers</th>
                        <th className="text-end">Used by</th>
                        <th>Updated</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {masterItems.map(m => (
                        <tr key={m._id}>
                          <td className="fw-medium">{m.name}</td>
                          <td>
                            {m.scopeLabel
                              ? <span className="badge bg-primary-transparent text-primary">{m.scopeLabel}</span>
                              : <span className="text-muted">—</span>
                            }
                          </td>
                          <td className="text-end font-monospace">{m.layerCount ?? 0}</td>
                          <td className="text-end text-muted small">
                            {m.linkedTemplates ?? 0} class template{(m.linkedTemplates ?? 0) !== 1 ? "s" : ""}
                          </td>
                          <td className="text-muted small">{new Date(m.updatedAt!).toLocaleDateString()}</td>
                          <td>
                            <div className="d-flex gap-1 justify-content-end">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => navigate(routes.reportCardDesignerMasterEdit.replace(":masterId", m._id))}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={async () => {
                                  try {
                                    await masterTemplatesApi.duplicate(m._id, `${m.name} (Copy)`)
                                    toast.success("Copied")
                                    fetchMasterTemplates()
                                  } catch { toast.error("Copy failed") }
                                }}
                              >
                                Copy
                              </button>
                              <button className="btn btn-sm btn-outline-danger" onClick={() => setDeleteMasterId(m._id)}>
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Modals ── */}
      <StartFromMasterModal
        open={showStartModal}
        masters={masterItems.length ? masterItems : []}
        onClose={() => setShowStartModal(false)}
        onPick={startNewWith}
      />

      <CloneModal
        source={cloneSource}
        sessions={sessions}
        terms={terms}
        classes={classes}
        onClose={() => setCloneSource(null)}
        onCloned={() => { setCloneSource(null); fetchClassTemplates() }}
      />

      <NewMasterModal
        open={showNewMasterModal}
        onClose={() => setShowNewMasterModal(false)}
        onCreated={(newMaster) => {
          setShowNewMasterModal(false)
          fetchMasterTemplates()
          navigate(routes.reportCardDesignerMasterEdit.replace(":masterId", newMaster._id))
        }}
      />

      <ConfirmModal
        open={!!deleteId}
        title="Delete Template?"
        body="This cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />

      <ConfirmModal
        open={!!deleteMasterId}
        title="Delete Master Template?"
        body="Class templates linked to this master will lose their master link."
        onConfirm={handleDeleteMaster}
        onCancel={() => setDeleteMasterId(null)}
      />
    </div>
  )
}

export default ReportCardDesigner
